import { get, post, put, API_BASE } from "@/lib/network";
import {
  AdminBillEntry,
  AdminMetrics,
  AdminOrder,
  AdminOrderEvent,
  AdminPaymentChannel,
  AdminRefund,
  AdminSystemConfig,
  PageResult,
  Product,
} from "@/lib/types";

function unwrap<T>(resp: any, fallback: T): T {
  if (resp?.success) {
    return resp.data as T;
  }
  return fallback;
}

export async function login(username: string, password: string) {
  const result = await post<any>("/login", { username, password });
  return result;
}

export async function getAdminMetrics(): Promise<AdminMetrics | null> {
  const result = await get<any>("/admin/commerce/dashboard/metrics");
  return result?.success ? (result.data as AdminMetrics) : null;
}

export async function getProducts(): Promise<Product[]> {
  const result = await get<any>("/admin/commerce/products");
  return unwrap<{ products: Product[] }>(result, { products: [] }).products;
}

export async function createProduct(payload: {
  product_code: string;
  name: string;
  status: string;
  definition_json: string;
  price_json: string;
  display_json: string;
}) {
  return post<any>("/admin/commerce/products", payload);
}

export async function updateProduct(payload: {
  product_id: number;
  name?: string;
  status?: string;
  definition_json?: string;
  price_json?: string;
  display_json?: string;
}) {
  return put<any>("/admin/commerce/products", payload);
}

function mapPaged<T>(data: any, aliasKey: string): PageResult<T> {
  return {
    list: (data?.list || data?.[aliasKey] || []) as T[],
    page: Number(data?.page || 1),
    page_size: Number(data?.page_size || 20),
    total: Number(data?.total || 0),
  };
}

export async function getAdminOrders(params: Record<string, any>): Promise<PageResult<AdminOrder>> {
  const result = await get<any>("/admin/commerce/orders", params);
  return mapPaged<AdminOrder>(result?.data, "orders");
}

export async function getAdminOrderDetail(orderNo: string): Promise<AdminOrder | null> {
  const result = await get<any>(`/admin/commerce/orders/${orderNo}`);
  return result?.success ? (result.data as AdminOrder) : null;
}

export async function getAdminOrderEvents(orderNo: string, params: Record<string, any>): Promise<PageResult<AdminOrderEvent>> {
  const result = await get<any>(`/admin/commerce/orders/${orderNo}/events`, params);
  return mapPaged<AdminOrderEvent>(result?.data, "events");
}

export async function getAdminOrderLedger(orderNo: string, params: Record<string, any>): Promise<PageResult<AdminBillEntry>> {
  const result = await get<any>(`/admin/commerce/orders/${orderNo}/ledger`, params);
  return mapPaged<AdminBillEntry>(result?.data, "ledger");
}

export async function getAdminBills(params: Record<string, any>): Promise<PageResult<AdminBillEntry>> {
  const result = await get<any>("/admin/commerce/bills", params);
  return mapPaged<AdminBillEntry>(result?.data, "bills");
}

export function exportAdminBills(params: Record<string, any>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") {
      search.set(k, String(v));
    }
  });
  return `${API_BASE}/admin/commerce/bills/export?${search.toString()}`;
}

export async function getAdminRefunds(params: Record<string, any>): Promise<PageResult<AdminRefund>> {
  const result = await get<any>("/admin/commerce/refunds", params);
  return mapPaged<AdminRefund>(result?.data, "refunds");
}

export async function approveRefund(refundNo: string, payload: { refund_type: string; approved_amount_fen: number; admin_note: string }) {
  return post<any>(`/admin/commerce/refunds/${refundNo}/approve`, payload);
}

export async function rejectRefund(refundNo: string, payload: { admin_note: string }) {
  return post<any>(`/admin/commerce/refunds/${refundNo}/reject`, payload);
}

export async function getPaymentChannels(): Promise<AdminPaymentChannel[]> {
  const result = await get<any>("/admin/commerce/payment/channels");
  return unwrap<{ channels: AdminPaymentChannel[] }>(result, { channels: [] }).channels;
}

export async function upsertChannel(payload: {
  channel_code: string;
  provider: string;
  status: string;
  config_json: any;
  remark: string;
}) {
  return post<any>("/admin/commerce/payment/channels/upsert", payload);
}

export async function getSystemConfigs(): Promise<AdminSystemConfig[]> {
  const result = await get<any>("/admin/commerce/system/configs");
  return unwrap<{ configs: AdminSystemConfig[] }>(result, { configs: [] }).configs;
}

export async function upsertSystemConfig(payload: {
  config_key: string;
  status: string;
  value_json: any;
  remark: string;
}) {
  return post<any>("/admin/commerce/system/configs/upsert", payload);
}
